/**
 * VendorQuickCreateDialog - Minimal dialog for quick vendor profile creation
 *
 * Fields: company (required), payment_terms (PaymentTermsSelect), currency
 * Redirects to detail page after creation.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Label } from '../../../components/ui/label';
import { DialogForm, CurrencyPicker } from '../../../shared/components/common';
import { PaymentTermsSelect } from '../../../shared/payment-terms';
import type { PaymentTermsValue } from '../../../shared/payment-terms';
import { OrganizationPicker } from '../../master_data/contacts';
import { useCreateVendor } from '../hooks/useVendors';
import type { VendorProfile } from '../types';

interface VendorQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select an organization (e.g. from the inline "Add profile" button in the list). */
  prefilledOrganizationId?: string;
  /**
   * Seed the organization picker's search with the text the user typed in the
   * picker they created from, so `Create "Acme"` lands on the Acme candidates
   * instead of an empty list.
   */
  prefilledOrganizationSearch?: string;
  /** Callback with the created vendor profile (before navigation). */
  onCreated?: (vendor: VendorProfile) => void;
  /** If true, don't navigate to the vendor detail page after creation. */
  skipNavigation?: boolean;
}

interface QuickCreateFormData {
  organization_id: string;
  payment_terms: PaymentTermsValue;
  currency_code: string;
}

const EMPTY_PAYMENT_TERMS: PaymentTermsValue = {
  payment_terms_id: null,
  payment_terms_basis: null,
  payment_terms_days: null,
};

export function VendorQuickCreateDialog({
  open,
  onClose,
  prefilledOrganizationId,
  prefilledOrganizationSearch,
  onCreated,
  skipNavigation = false,
}: VendorQuickCreateDialogProps) {
  const { t } = useTranslation(['procurement', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateVendor();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      organization_id: prefilledOrganizationId ?? '',
      payment_terms: EMPTY_PAYMENT_TERMS,
      currency_code: 'USD',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        organization_id: prefilledOrganizationId ?? '',
        payment_terms: EMPTY_PAYMENT_TERMS,
        currency_code: 'USD',
      });
    }
  }, [open, reset, prefilledOrganizationId]);

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const vendor = await createMutation.mutateAsync({
        organization_id: data.organization_id,
        payment_terms_id: data.payment_terms.payment_terms_id ?? undefined,
        payment_terms_basis: data.payment_terms.payment_terms_basis ?? undefined,
        payment_terms_days: data.payment_terms.payment_terms_days ?? undefined,
        currency_code: data.currency_code || undefined,
      });
      onCreated?.(vendor);
      onClose();
      if (!skipNavigation) {
        navigate(`/procurement/vendors/${vendor.id}`);
      }
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('vendors.createProfile')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="organization_id" required>{t('vendors.company')}</Label>
          <Controller
            name="organization_id"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <OrganizationPicker
                value={field.value}
                onChange={(id) => field.onChange(id || '')}
                organizationRelationshipFilter="vendor"
                defaultRelationships={['vendor']}
                initialSearch={prefilledOrganizationSearch}
              />
            )}
          />
          {errors.organization_id && (
            <p className="text-sm text-destructive">{errors.organization_id.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('vendors.paymentTerms')}</Label>
          <Controller
            name="payment_terms"
            control={control}
            render={({ field }) => (
              <PaymentTermsSelect
                value={field.value}
                onChange={field.onChange}
                allowCustom
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="currency_code">{t('vendors.currency')}</Label>
          <Controller
            name="currency_code"
            control={control}
            render={({ field }) => (
              <CurrencyPicker
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {t('vendors.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
